<template>
  <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Organization Settings</h1>
        <p class="text-gray-600">Manage your organization and team members</p>
      </div>

      <!-- Organization Details Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Organization Details</h2>
        
        <div v-if="loading" class="animate-pulse">
          <div class="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div class="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
        
        <div v-else-if="organization" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Organization Name</label>
            <div class="flex items-center gap-2">
              <input
                v-if="editingName"
                v-model="editedName"
                type="text"
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                @keyup.enter="saveOrganizationName"
                @keyup.escape="cancelEditName"
              />
              <p v-else class="flex-1 text-gray-900">{{ organization.name }}</p>
              
              <button
                v-if="!editingName && isOwner"
                @click="startEditName"
                class="text-primary hover:text-primary-dark"
              >
                <PhPencilSimple :size="20" />
              </button>
              <div v-else-if="editingName" class="flex gap-2">
                <button
                  @click="saveOrganizationName"
                  class="text-green-600 hover:text-green-700"
                  :disabled="savingName"
                >
                  <PhCheck :size="20" />
                </button>
                <button
                  @click="cancelEditName"
                  class="text-red-600 hover:text-red-700"
                >
                  <PhX :size="20" />
                </button>
              </div>
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
            <p class="text-gray-600 font-mono text-sm">{{ organization.id }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Owner</label>
            <p class="text-gray-900">{{ organization.owner_name || organization.owner_id }}</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Created</label>
            <p class="text-gray-900">{{ formatDate(organization.created_at) }}</p>
          </div>
        </div>
        
        <div v-else-if="!organization && !loading" class="text-center py-8">
          <p class="text-gray-600 mb-4">No organization found. Create one to get started.</p>
          <button
            @click="createOrganization"
            class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
            :disabled="creatingOrg"
          >
            {{ creatingOrg ? 'Creating...' : 'Create Organization' }}
          </button>
        </div>
      </div>

      <!-- Team Members Card -->
      <div v-if="organization" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold text-gray-900">Team Members</h2>
          <button
            v-if="isOwner"
            @click="showInviteModal = true"
            class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center gap-2"
          >
            <PhUserPlus :size="20" />
            Invite Member
          </button>
        </div>

        <!-- Members List -->
        <div v-if="loadingMembers" class="animate-pulse space-y-3">
          <div v-for="i in 3" :key="i" class="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div>
                <div class="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                <div class="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="members.length > 0" class="space-y-3">
          <div
            v-for="member in members"
            :key="member.id"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                <span class="text-primary font-medium">
                  {{ (member.display_name || member.email || '?').charAt(0).toUpperCase() }}
                </span>
              </div>
              <div>
                <p class="font-medium text-gray-900">{{ member.display_name || member.email }}</p>
                <p class="text-sm text-gray-600">{{ member.role }}</p>
              </div>
            </div>
            
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-500">Joined {{ formatDate(member.joined_at) }}</span>
              <button
                v-if="isOwner && member.user_id !== organization.owner_id"
                @click="removeMember(member)"
                class="text-red-600 hover:text-red-700"
                title="Remove member"
              >
                <PhTrash :size="20" />
              </button>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-8 text-gray-500">
          No team members yet. Invite someone to collaborate!
        </div>
      </div>

      <!-- Pending Invitations Card -->
      <div v-if="organization && invitations.length > 0" class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Pending Invitations</h2>
        
        <div class="space-y-3">
          <div
            v-for="invite in invitations"
            :key="invite.id"
            class="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md"
          >
            <div class="flex-1">
              <p class="font-medium text-gray-900">
                {{ invite.email || 'Open invitation' }}
                <span class="text-sm text-gray-600 ml-2">({{ invite.role }})</span>
              </p>
              <p class="text-sm text-gray-600">
                Expires {{ formatDate(invite.expires_at) }}
              </p>
            </div>
            
            <div class="flex items-center gap-2">
              <button
                @click="copyInviteLink(invite)"
                class="text-primary hover:text-primary-dark"
                title="Copy invite link"
              >
                <PhClipboard :size="20" />
              </button>
              <button
                v-if="isOwner()"
                @click="cancelInvitation(invite)"
                class="text-red-600 hover:text-red-700"
                title="Cancel invitation"
              >
                <PhX :size="20" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Invite Modal -->
      <Teleport to="body">
        <div
          v-if="showInviteModal"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          @click.self="showInviteModal = false"
        >
          <div class="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Invite Team Member</h3>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Email Address (optional)
                </label>
                <input
                  v-model="inviteEmail"
                  type="email"
                  placeholder="colleague@example.com"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p class="text-xs text-gray-500 mt-1">
                  Leave blank to create an open invitation link anyone can use
                </p>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  v-model="inviteRole"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              
              <div v-if="inviteError" class="text-red-600 text-sm">
                {{ inviteError }}
              </div>

              <!-- Show invite link after creation -->
              <div v-if="currentInviteLink" class="bg-green-50 border border-green-200 rounded-md p-4">
                <p class="text-sm font-medium text-green-900 mb-2">Invitation link created!</p>
                <div class="flex items-center gap-2">
                  <input
                    :value="currentInviteLink"
                    readonly
                    class="flex-1 px-2 py-1 text-sm bg-white border border-green-300 rounded"
                  />
                  <button
                    @click="copyToClipboard(currentInviteLink)"
                    class="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Copy
                  </button>
                </div>
                <p class="text-xs text-gray-600 mt-2">
                  This link expires in 7 days. Team members can use it to join without creating their own Stripe account.
                </p>
              </div>
            </div>
            
            <div class="flex justify-end gap-3 mt-6">
              <button
                @click="closeInviteModal"
                class="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                {{ currentInviteLink ? 'Done' : 'Cancel' }}
              </button>
              <button
                v-if="!currentInviteLink"
                @click="createInvitation"
                class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                :disabled="inviting"
              >
                {{ inviting ? 'Creating...' : 'Create Invite Link' }}
              </button>
            </div>
          </div>
        </div>
      </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import {
  PhPencilSimple,
  PhCheck,
  PhX,
  PhUserPlus,
  PhTrash,
  PhClipboard
} from '@phosphor-icons/vue';

interface Organization {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  owner_name?: string;
  created_at: string;
  updated_at: string;
}

interface Member {
  id: string;
  user_id: string;
  organization_id: string;
  role: string;
  email?: string;
  display_name?: string;
  joined_at: string;
}

interface Invitation {
  id: string;
  organization_id: string;
  email?: string;
  role: string;
  invite_code: string;
  expires_at: string;
  created_at: string;
}

const authStore = useAuthStore();

// Component state
const organization = ref<Organization | null>(null);
const members = ref<Member[]>([]);
const invitations = ref<Invitation[]>([]);
const loading = ref(true);
const loadingMembers = ref(false);
const editingName = ref(false);
const editedName = ref('');
const savingName = ref(false);
const creatingOrg = ref(false);
const showInviteModal = ref(false);
const inviteEmail = ref('');
const inviteRole = ref('member');
const inviting = ref(false);
const inviteError = ref('');
const currentInviteLink = ref('');

// Computed
const user = authStore.user;
const isOwner = () => organization.value?.owner_id === user?.id;

// Helper function to get auth token
function getAuthToken(): string | null {
  const authCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('ha_token='));
  
  if (!authCookie) return null;
  
  try {
    // URL decode the cookie value first, then base64 decode
    const encodedToken = authCookie.split('=')[1];
    const decodedToken = decodeURIComponent(encodedToken);
    const tokenData = JSON.parse(atob(decodedToken));
    return tokenData.access_token || tokenData.token;
  } catch (e) {
    console.error('Error parsing auth token:', e);
    // Fallback: try using authStore if available
    return authStore.accessToken || null;
  }
}

// Methods
async function fetchOrganization() {
  loading.value = true;
  try {
    const token = getAuthToken();
    if (!token) {
      console.error('No auth token found');
      loading.value = false;
      return;
    }
    
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data) {
        organization.value = data.data;
        fetchMembers();
        fetchInvitations();
      }
    }
  } catch (error) {
    console.error('Error fetching organization:', error);
  } finally {
    loading.value = false;
  }
}

async function fetchMembers() {
  if (!organization.value) return;
  
  loadingMembers.value = true;
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization/members`,
      {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        members.value = data.data || [];
      }
    }
  } catch (error) {
    console.error('Error fetching members:', error);
  } finally {
    loadingMembers.value = false;
  }
}

async function createOrganization() {
  creatingOrg.value = true;
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: user.value?.display_name ? `${user.value.display_name}'s Organization` : 'My Organization'
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        organization.value = data.data;
        fetchMembers();
      }
    }
  } catch (error) {
    console.error('Error creating organization:', error);
  } finally {
    creatingOrg.value = false;
  }
}

function startEditName() {
  editingName.value = true;
  editedName.value = organization.value?.name || '';
}

function cancelEditName() {
  editingName.value = false;
  editedName.value = '';
}

async function saveOrganizationName() {
  if (!editedName.value || !organization.value) return;
  
  savingName.value = true;
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editedName.value
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        organization.value.name = editedName.value;
        editingName.value = false;
      }
    }
  } catch (error) {
    console.error('Error updating organization:', error);
  } finally {
    savingName.value = false;
  }
}

async function fetchInvitations() {
  if (!organization.value) return;
  
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization/invitations`,
      {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        invitations.value = data.data || [];
      }
    }
  } catch (error) {
    console.error('Error fetching invitations:', error);
  }
}

async function createInvitation() {
  if (!organization.value) return;
  
  inviting.value = true;
  inviteError.value = '';
  
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization/invite`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: inviteEmail.value || null,
          role: inviteRole.value
        })
      }
    );

    const data = await response.json();
    
    if (response.ok && data.success) {
      currentInviteLink.value = data.data.inviteUrl;
      fetchInvitations(); // Refresh invitations list
    } else {
      inviteError.value = data.error || 'Failed to create invitation';
    }
  } catch (error) {
    console.error('Error creating invitation:', error);
    inviteError.value = 'Failed to create invitation';
  } finally {
    inviting.value = false;
  }
}

async function cancelInvitation(invite: Invitation) {
  if (!confirm('Cancel this invitation?')) return;
  
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization/invitations/${invite.id}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      invitations.value = invitations.value.filter(i => i.id !== invite.id);
    }
  } catch (error) {
    console.error('Error canceling invitation:', error);
  }
}

function copyInviteLink(invite: Invitation) {
  // Use production URL for invites, even in development
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const inviteUrl = `${baseUrl}/join?code=${invite.invite_code}`;
  copyToClipboard(inviteUrl);
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(() => {
    // You could show a toast notification here
    console.log('Copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy:', err);
  });
}

function closeInviteModal() {
  showInviteModal.value = false;
  inviteEmail.value = '';
  inviteRole.value = 'member';
  inviteError.value = '';
  currentInviteLink.value = '';
}

async function removeMember(member: Member) {
  if (!confirm(`Remove ${member.display_name || member.email} from the organization?`)) {
    return;
  }
  
  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/organization/members/${member.id}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      members.value = members.value.filter(m => m.id !== member.id);
    }
  } catch (error) {
    console.error('Error removing member:', error);
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// Lifecycle
onMounted(() => {
  fetchOrganization();
});
</script>

<style scoped>
.text-primary {
  color: #8b5cf6;
}

.bg-primary {
  background-color: #8b5cf6;
}

.bg-primary\/20 {
  background-color: rgba(139, 92, 246, 0.2);
}

.hover\:bg-primary-dark:hover {
  background-color: #7c3aed;
}

.text-primary-dark {
  color: #7c3aed;
}

.hover\:text-primary-dark:hover {
  color: #7c3aed;
}
</style>